import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Search, Trash2, Save, Brain, Sparkles, AlertTriangle,
  Power, PowerOff, CheckSquare, Square,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/* ────────────────────────────────────────────────────────────────────────
   Rule Management — manage learned bank-import mappings.
   Every confirmed mapping during a bulk-post is stored here. This page
   lets the user re-target the ledger, deactivate (soft-disable), bulk
   delete obsolete mappings, and see how often each pattern fires.
   Route: /banking/smart-settings (alias: /banking/rules)
   ──────────────────────────────────────────────────────────────────────── */

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [mappings, setMappings] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  // Pending ledger edits per pattern: { [pattern]: ledger_id }
  const [drafts, setDrafts] = useState({});
  // Selected patterns for bulk operations
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      const [m, l] = await Promise.all([
        api.get(`/companies/${activeId}/bank/mappings`),
        api.get(`/companies/${activeId}/ledgers`),
      ]);
      setMappings(m.data || []);
      setLedgers(l.data || []);
      setSelected(new Set());
    } catch (e) {
      toast.error("Failed to load rules");
    } finally {
      setBusy(false);
    }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mappings.filter((m) => {
      if (statusFilter === "active" && m.active === false) return false;
      if (statusFilter === "inactive" && m.active !== false) return false;
      if (!q) return true;
      return (
        m.pattern.toLowerCase().includes(q) ||
        (m.ledger_name || "").toLowerCase().includes(q)
      );
    });
  }, [mappings, search, statusFilter]);

  const activeCount = useMemo(
    () => mappings.filter((m) => m.active !== false).length,
    [mappings],
  );

  const saveMapping = async (pattern) => {
    const lid = drafts[pattern];
    if (!lid) return;
    try {
      await api.patch(
        `/companies/${activeId}/bank/mappings/${encodeURIComponent(pattern)}`,
        { ledger_id: lid },
      );
      toast.success("Rule updated");
      setDrafts((d) => { const next = { ...d }; delete next[pattern]; return next; });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const toggleActive = async (pattern, next) => {
    try {
      await api.patch(
        `/companies/${activeId}/bank/mappings/${encodeURIComponent(pattern)}`,
        { active: next },
      );
      toast.success(next ? "Rule activated" : "Rule deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Toggle failed");
    }
  };

  const deleteMapping = async (pattern) => {
    if (!window.confirm(`Delete rule "${pattern}"?\n\nFuture imports of this narration will fall back to the rule engine / AI.`)) return;
    try {
      await api.delete(`/companies/${activeId}/bank/mappings/${encodeURIComponent(pattern)}`);
      toast.success("Rule deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleRow = (pattern) => {
    setSelected((s) => {
      const next = new Set(s);
      next.has(pattern) ? next.delete(pattern) : next.add(pattern);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((s) => {
      const visiblePatterns = filtered.map((m) => m.pattern);
      const allSelected = visiblePatterns.every((p) => s.has(p));
      const next = new Set(s);
      if (allSelected) visiblePatterns.forEach((p) => next.delete(p));
      else visiblePatterns.forEach((p) => next.add(p));
      return next;
    });
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    const patterns = [...selected];
    const label = action === "delete" ? "Delete" : action === "activate" ? "Activate" : "Deactivate";
    if (action === "delete") {
      if (!window.confirm(`Delete ${patterns.length} rule${patterns.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    }
    try {
      const res = await api.post(
        `/companies/${activeId}/bank/mappings/bulk-action`,
        { patterns, action },
      );
      const affected = res.data?.affected ?? patterns.length;
      toast.success(`${label}: ${affected} rule${affected === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || `${label} failed`);
    }
  };

  const visiblePatterns = useMemo(() => filtered.map((m) => m.pattern), [filtered]);
  const allVisibleSelected = visiblePatterns.length > 0 && visiblePatterns.every((p) => selected.has(p));

  return (
    <>
      <PageHeader
        title="Rule Management"
        subtitle={`Manage learned bank-import rules · ${mappings.length} total · ${activeCount} active`}
        actions={
          <Button variant="ghost" onClick={() => nav("/banking")} className="h-8 text-sm" data-testid="btn-back-banking">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Banking
          </Button>
        }
      />

      <div className="p-6 space-y-4">
        {mappings.length === 0 && !busy ? (
          <div className="border border-dashed border-gray-300 rounded bg-white p-10 text-center" data-testid="empty-state">
            <Brain className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <div className="text-sm text-gray-700 font-medium">No rules learned yet.</div>
            <div className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
              Upload a bank statement at <strong>Banking → AI Import + Auto Vouchers</strong> and approve a few rows.
              Every approval is remembered here as a rule and applied to future imports automatically.
            </div>
            <Button
              onClick={() => nav("/banking/import-recommend")}
              className="mt-4 h-8 text-xs bg-purple-700 hover:bg-purple-800"
              data-testid="btn-go-import"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Go to Bank Import
            </Button>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="border border-gray-200 rounded bg-white p-2.5 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <Input
                  placeholder="Search pattern or ledger…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 pl-7 text-sm"
                  data-testid="input-search-mappings"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 rounded border border-gray-300 text-xs px-2"
                data-testid="select-status-filter"
              >
                <option value="all">All ({mappings.length})</option>
                <option value="active">Active ({activeCount})</option>
                <option value="inactive">Inactive ({mappings.length - activeCount})</option>
              </select>
              <span className="text-[11px] text-gray-500 ml-auto">
                {filtered.length} visible · sorted by frequency
              </span>
            </div>

            {/* Bulk action bar (only shows when rows are selected) */}
            {selected.size > 0 && (
              <div
                className="border border-purple-300 rounded bg-purple-50 p-2 flex items-center gap-2"
                data-testid="bulk-action-bar"
              >
                <span className="text-xs text-purple-800 font-medium">
                  {selected.size} rule{selected.size === 1 ? "" : "s"} selected
                </span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <button
                    onClick={() => bulkAction("activate")}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 text-[11px]"
                    data-testid="btn-bulk-activate"
                  >
                    <Power className="w-3 h-3" /> Activate
                  </button>
                  <button
                    onClick={() => bulkAction("deactivate")}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 text-[11px]"
                    data-testid="btn-bulk-deactivate"
                  >
                    <PowerOff className="w-3 h-3" /> Deactivate
                  </button>
                  <button
                    onClick={() => bulkAction("delete")}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 text-[11px]"
                    data-testid="btn-bulk-delete"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                  <button
                    onClick={() => setSelected(new Set())}
                    className="text-[11px] text-purple-700 underline ml-2"
                    data-testid="btn-clear-selection"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="border border-gray-200 rounded bg-white overflow-hidden">
              <table className="tx-table text-xs">
                <thead>
                  <tr>
                    <th className="w-8">
                      <button
                        onClick={toggleAllVisible}
                        title={allVisibleSelected ? "Deselect all" : "Select all visible"}
                        data-testid="btn-toggle-all"
                      >
                        {allVisibleSelected
                          ? <CheckSquare className="w-3.5 h-3.5 text-purple-700" />
                          : <Square className="w-3.5 h-3.5 text-gray-400" />}
                      </button>
                    </th>
                    <th>Narration Pattern</th>
                    <th className="w-72">Mapped Ledger</th>
                    <th className="w-20 text-right">Used</th>
                    <th className="w-28">Last Used</th>
                    <th className="w-20 text-center">Status</th>
                    <th className="w-40 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">No rules match the filter.</td></tr>
                  )}
                  {filtered.map((m) => {
                    const draftedLid = drafts[m.pattern];
                    const currentLid = draftedLid || m.ledger_id;
                    const hasDraft = draftedLid && draftedLid !== m.ledger_id;
                    const ledgerStillExists = ledgers.some((L) => L.id === m.ledger_id);
                    const isActive = m.active !== false;
                    const isSelected = selected.has(m.pattern);
                    const safeId = m.pattern.slice(0, 20);
                    return (
                      <tr
                        key={m.pattern}
                        data-testid={`row-mapping-${safeId}`}
                        className={!isActive ? "bg-slate-50/70 text-slate-500" : ""}
                      >
                        <td>
                          <button
                            onClick={() => toggleRow(m.pattern)}
                            title="Select for bulk action"
                            data-testid={`chk-mapping-${safeId}`}
                          >
                            {isSelected
                              ? <CheckSquare className="w-3.5 h-3.5 text-purple-700" />
                              : <Square className="w-3.5 h-3.5 text-gray-400" />}
                          </button>
                        </td>
                        <td>
                          <div className="font-mono text-[11px] break-all">{m.pattern}</div>
                          {!ledgerStillExists && (
                            <div className="flex items-center gap-1 text-[10px] text-amber-700 mt-0.5">
                              <AlertTriangle className="w-3 h-3" />
                              Mapped ledger no longer exists — re-assign or delete.
                            </div>
                          )}
                        </td>
                        <td>
                          <select
                            value={currentLid}
                            onChange={(e) => setDrafts((d) => ({ ...d, [m.pattern]: e.target.value }))}
                            className="h-7 w-full rounded border border-gray-300 px-1.5 text-[11px]"
                            data-testid={`select-mapping-${safeId}`}
                          >
                            {!ledgerStillExists && <option value={m.ledger_id}>{m.ledger_name} (deleted)</option>}
                            {ledgers.map((L) => (
                              <option key={L.id} value={L.id}>{L.name} · {L.group}</option>
                            ))}
                          </select>
                        </td>
                        <td className="tx-num font-mono">{m.frequency || 1}</td>
                        <td className="text-[10px] font-mono text-gray-500">
                          {m.last_used ? m.last_used.slice(0, 10) : "—"}
                        </td>
                        <td className="text-center">
                          {isActive ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-medium"
                              data-testid={`badge-active-${safeId}`}
                            >
                              <Power className="w-2.5 h-2.5" /> Active
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-medium"
                              data-testid={`badge-inactive-${safeId}`}
                            >
                              <PowerOff className="w-2.5 h-2.5" /> Inactive
                            </span>
                          )}
                        </td>
                        <td className="text-right">
                          <div className="inline-flex items-center gap-1">
                            {hasDraft && (
                              <button
                                onClick={() => saveMapping(m.pattern)}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 text-[10px]"
                                title="Save change"
                                data-testid={`btn-save-${safeId}`}
                              >
                                <Save className="w-3 h-3" /> Save
                              </button>
                            )}
                            <button
                              onClick={() => toggleActive(m.pattern, !isActive)}
                              className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] ${
                                isActive
                                  ? "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                                  : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                              }`}
                              title={isActive ? "Deactivate rule" : "Activate rule"}
                              data-testid={`btn-toggle-${safeId}`}
                            >
                              {isActive ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={() => deleteMapping(m.pattern)}
                              className="inline-flex items-center text-red-600 hover:text-red-800 px-1.5 py-0.5"
                              title="Delete rule"
                              data-testid={`btn-delete-${safeId}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-[11px] text-gray-500 italic flex items-center gap-1">
              <Brain className="w-3 h-3" />
              Patterns are stored with digits and refs stripped, so different transactions
              from the same party collapse to the same key. Deactivating a rule keeps it in
              the list but skips it on future imports — useful when you need to temporarily
              disable a rule without losing its history.
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function BankSmartSettings() {
  return <RequireCompany><Inner /></RequireCompany>;
}
